import type {ReactNode} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton, useFocusTrap, useOnEscapePress} from '@primer/react'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import styles from './Panel.module.css'
import {PanelNavigationDropdown} from './PanelNavigationDropdown'
import {restorePanelFocusAfterClose} from './panel-focus'
import {
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
} from '../../contexts/DockedPanelContext'
import type {DockedPanel} from '../../contexts/DockedPanelContext'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useViewportFitHeight} from '../../hooks/use-viewport-fit-height'
import {useDialogFillHeight} from '../../hooks/use-dialog-fill-height'
import {isFeatureEnabled} from '@github-ui/feature-flags'

interface PanelHeaderProps {
  children: ReactNode
}

function PanelHeader({children}: PanelHeaderProps) {
  return <>{children}</>
}

interface PanelBodyProps {
  children: ReactNode
  className?: string
  /**
   * When false, the body container does not scroll itself. Use when the
   * panel content includes its own scrollable subsection (e.g. only the
   * lower half should scroll while a sticky form/header stays fixed at
   * the top). Defaults to true to preserve existing behavior.
   */
  scrollable?: boolean
}

function PanelBody({children, className, scrollable = true}: PanelBodyProps) {
  return (
    <div className={clsx('flex-1', scrollable ? 'overflow-auto' : 'overflow-hidden', className ?? 'tmp-p-3')}>
      {children}
    </div>
  )
}

interface PanelFooterProps {
  children: ReactNode
}

function PanelFooter({children}: PanelFooterProps) {
  return <>{children}</>
}

interface PanelViewportFillProps {
  children: ReactNode
  fixedHeight?: boolean
  bottomInsetPx?: number
}

/**
 * Opt-in wrapper that constrains its children to the visible viewport area.
 *
 * Use inside `Panel.Body` when the panel content has its own internal scroll
 * management (e.g. a chat with a sticky input at the bottom) and must not
 * extend below the viewport -- even before the sticky sidebar fully takes effect.
 *
 * When `fixedHeight` is true the height is measured on mount and window resize
 * only, preventing content from shifting as the global header scrolls in/out.
 */
function PanelViewportFill({children, fixedHeight, bottomInsetPx}: PanelViewportFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useViewportFitHeight(ref, {
    recalculateOnScroll: !fixedHeight,
    bottomInsetPx,
  })
  return (
    <div ref={ref} className={styles.ViewportFill}>
      {children}
    </div>
  )
}

interface PanelDialogFillProps {
  children: ReactNode
}

/**
 * Wrapper for Dialog body content that fills the available height.
 *
 * Primer's Dialog body gets its height from flex layout, which breaks
 * CSS percentage-height resolution for children.  This component
 * uses a ResizeObserver on the parent to set an explicit pixel height.
 */
function PanelDialogFill({children}: PanelDialogFillProps) {
  const ref = useRef<HTMLDivElement>(null)
  useDialogFillHeight(ref)
  return (
    <div ref={ref} className={styles.DialogFill}>
      {children}
    </div>
  )
}

interface PanelProps {
  panelName: DockedPanel
  children: ReactNode
  headerActions?: ReactNode
}

/**
 * Self-contained compound component for docked side panel content.
 *
 * Renders the panel heading (derived from `panelName`), a close button with
 * managed focus refs, and `Panel.Header`, `Panel.Body`, and `Panel.Footer`
 * slot sub-components laid out in a flex column. The body region scrolls
 * while header and footer remain fixed.
 *
 * Can be rendered directly inside `DockedSidePanelContent` or portaled
 * into the docked panel's content slot via `createPortal`.
 */
function Panel({panelName: _panelName, children, headerActions}: PanelProps) {
  const {closeButtonRef, returnFocusRef} = useDockedPanelFocusRefs()
  const {closePanel} = useDockedPanelActions()
  const isCompact = useIsDockedPanelCompact()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(() => window.matchMedia('(max-width: calc(48rem - 0.02px))').matches)

  const handleClose = useCallback(() => {
    restorePanelFocusAfterClose(returnFocusRef.current)
    closePanel()
  }, [closePanel, returnFocusRef])

  const [slots] = useSlots(children, {
    header: PanelHeader,
    body: PanelBody,
    footer: PanelFooter,
  })

  useFocusTrap({containerRef, disabled: !isFullscreen})

  // Only close on Escape if focus is within the panel
  useOnEscapePress(() => {
    if (containerRef.current?.contains(document.activeElement)) {
      handleClose()
    }
  }, [handleClose])

  // When SplitPageLayout.Sidebar transitions to fullscreen,
  // move focus to the close button so focus isn't behind the panel.
  useEffect(() => {
    const mediaQuery = ssrSafeWindow?.matchMedia('(max-width: calc(48rem - 0.02px))')

    const handleChange = (event: MediaQueryListEvent) => {
      setIsFullscreen(event.matches)
      if (event.matches && !containerRef.current?.contains(document.activeElement)) {
        closeButtonRef.current?.focus()
      }
    }

    mediaQuery?.addEventListener('change', handleChange)
    return () => mediaQuery?.removeEventListener('change', handleChange)
  }, [closeButtonRef])

  const layoutContainmentEnabled = isFeatureEnabled('pull_request_css_layout_containment')

  return (
    <div
      ref={containerRef}
      className={clsx('d-flex flex-column height-full', layoutContainmentEnabled && styles.containLayout)}
    >
      {/* In compact mode the Dialog provides the heading and close button */}
      {(!isCompact || slots.header) && (
        <div className={styles.Header}>
          {!isCompact && (
            <div className={styles.HeaderContent}>
              <Heading as="h3" className={styles.Heading}>
                <PanelNavigationDropdown />
              </Heading>
              <div className="d-flex flex-items-center">
                {headerActions}
                <IconButton
                  ref={closeButtonRef}
                  aria-label="Close side panel"
                  icon={XIcon}
                  variant="invisible"
                  size="medium"
                  onClick={handleClose}
                />
              </div>
            </div>
          )}
          {slots.header}
        </div>
      )}
      {slots.body}
      {slots.footer && <div className="tmp-p-3">{slots.footer}</div>}
    </div>
  )
}

Panel.Header = PanelHeader
Panel.Body = PanelBody
Panel.Footer = PanelFooter
Panel.ViewportFill = PanelViewportFill
Panel.DialogFill = PanelDialogFill

export {Panel}
